import { Box, Typography } from '@mui/material';
import { SOCIAL_NORMALIZERS } from '@porter/shared-theme';
import { BookingSiteSocials } from '../../../types/bookingSite';

interface Props {
  socials: BookingSiteSocials;
  /** Fields the last publish was refused over. */
  rejected?: (keyof BookingSiteSocials)[];
  onChange: (changes: Partial<BookingSiteSocials>) => void;
}

const HANDLE_ERROR = 'Enter your handle, or a link to your profile page.';
const URL_ERROR = 'Enter a web address, like yoursite.com';

const ROWS = [
  {
    key: 'instagramHandle' as const,
    label: 'Instagram',
    // The @ is a fixed prefix on the field, not something to type.
    prefix: '@',
    placeholder: 'yourshop',
  },
  {
    key: 'tiktokHandle' as const,
    label: 'TikTok',
    prefix: '@',
    placeholder: 'yourshop',
  },
  {
    key: 'facebookUrl' as const,
    label: 'Facebook',
    placeholder: 'facebook.com/yourshop',
  },
  {
    key: 'websiteUrl' as const,
    label: 'Website',
    placeholder: 'yourshop.com',
  },
];

function SocialsSection({ socials, rejected = [], onChange }: Props) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <Typography
        variant="body2"
        sx={{ margin: '0 0 2px', color: 'text.secondary' }}>
        These show on your booking site and on every artist page in the shop.
      </Typography>
      {ROWS.map(row => {
        const error = rejected.includes(row.key)
          ? row.prefix
            ? HANDLE_ERROR
            : URL_ERROR
          : null;

        return (
          <Box
            key={row.key}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <Box
              component="label"
              htmlFor={`social-${row.key}`}
              sx={{
                width: 78,
                flex: 'none',
                color: 'text.primary',
                typography: 'h5',
              }}>
              {row.label}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid',
                  borderColor: error ? 'error.main' : 'rgba(0, 0, 0, 0.1)',
                  borderRadius: '5px',
                  background: '#fff',
                  '&:focus-within': { borderColor: 'primary.main' },
                }}>
                {!!row.prefix && (
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ pl: '14px', color: 'text.tertiary' }}>
                    {row.prefix}
                  </Typography>
                )}
                <Box
                  id={`social-${row.key}`}
                  component="input"
                  value={socials[row.key] || ''}
                  placeholder={row.placeholder}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    // Only the prefix comes off while typing; the rest waits for blur.
                    onChange({
                      [row.key]: row.prefix
                        ? event.target.value.replace(/^@+/, '')
                        : event.target.value,
                    })
                  }
                  onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                    onChange({
                      [row.key]: SOCIAL_NORMALIZERS[row.key](
                        event.target.value,
                      ),
                    })
                  }
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    border: 'none',
                    outline: 'none',
                    borderRadius: '5px',
                    padding: row.prefix ? '10px 14px 10px 3px' : '10px 14px',
                    font: 'inherit',
                    fontFamily: 'inherit',
                    color: 'text.primary',
                    background: 'transparent',
                    typography: 'body2',
                  }}
                />
              </Box>
              {!!error && (
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: '4px', color: 'error.main' }}>
                  {error}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default SocialsSection;
